/**
 * Message Send Log Service
 *
 * Data access layer for message_send_log table.
 * Tracks SMS and WhatsApp message delivery status for bookings.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import type { Database } from '../lib/database.types';

export type MessageSendLog = Database['public']['Tables']['message_send_log']['Row'];

export type MessageChannel = 'sms' | 'whatsapp';

export type MessageStatus = 'pending' | 'sent' | 'accepted' | 'delivered' | 'read' | 'failed' | 'undelivered' | 'invalidated';

/**
 * Get message send logs for a booking, optionally filtered by channel.
 */
export async function getMessageSendLogs(bookingId: string, channel?: MessageChannel) {
  try {
    let query = supabase
      .from('message_send_log')
      .select('*')
      .eq('booking_id', bookingId)
      .order('target_send_at', { ascending: true });

    if (channel) {
      query = query.eq('channel', channel);
    } else {
      // Only SMS and WhatsApp — exclude email channel
      query = query.in('channel', ['sms', 'whatsapp']);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { data: data as MessageSendLog[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get status badge class based on message status.
 */
export function getMessageStatusBadgeClass(status: string): string {
  switch (status.toLowerCase()) {
    case 'delivered':
    case 'read':
      return 'badge-success';
    case 'sent':
    case 'accepted':
      return 'badge-info';
    case 'pending':
      return 'badge-warning';
    case 'failed':
    case 'undelivered':
      return 'badge-danger';
    case 'invalidated':
      return 'badge-warning';
    default:
      return 'badge-info';
  }
}

/**
 * Format status for display.
 */
export function formatMessageStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}
